import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { ListFilterOption } from '@common/ui/ListFilters/common';

import ListFilterMultiSelect from '@common/ui/ListFilters/ListFilterMultiSelect';
import ListFilterSelectChipTrigger from '@common/ui/ListFilters/ListFilterSelectChipTrigger';
import { useListIntegrationFilterOptions } from '@client-app-sdk/hooks/filters';
import Popover from '@mui/material/Popover';
import { Box } from '@mui/material';
import { PAGE_FILTER_PARAM } from './MyAccessFilters';

export interface IntegrationFilterAsyncOption {
  value: string;
  label: string;
  icon: string;
  searchHelper?: string;
}

export const INTEGRATION_FILTER_LABEL = 'Integration';
export const INTEGRATION_FILTER_PARAM = 'integration';

export default function IntegrationFilterAsync() {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedQp = searchParams.getAll(INTEGRATION_FILTER_PARAM);

  const [enabled] = useState(() => selectedQp.length > 0);

  const {
    data: integrationFilters,
    isFetched,
    isFetching,
    refetch,
  } = useListIntegrationFilterOptions({
    scope: 'access_sessions',
    limit: 1000,
    enabled,
  });

  const options = useMemo(() => {
    const flattenedIntegrations = integrationFilters?.pages?.flatMap((page) => page.data) || [];
    return flattenedIntegrations.map((integration) => ({
      value: integration.id,
      label: integration.name,
      icon: integration.icons.svg,
      searchHelper: `${integration.name}`,
    }));
  }, [integrationFilters]);

  const selectedOptions = useMemo(() => {
    const list: ListFilterOption[] = [];
    for (const option of options) {
      if (selectedQp.includes(option.value)) {
        list.push(option);
      }
    }
    return list;
  }, [options, selectedQp]);

  const handleOnSelect = (values: ListFilterOption[]) => {
    searchParams.delete(INTEGRATION_FILTER_PARAM);
    for (const value of values) {
      searchParams.append(INTEGRATION_FILTER_PARAM, value.value);
    }
    if (searchParams.get(PAGE_FILTER_PARAM)) {
      searchParams.delete(PAGE_FILTER_PARAM);
    }
    setSearchParams(searchParams);
  };

  const handleOnClear = () => {
    searchParams.delete(INTEGRATION_FILTER_PARAM);
    if (searchParams.get(PAGE_FILTER_PARAM)) {
      searchParams.delete(PAGE_FILTER_PARAM);
    }
    setSearchParams(searchParams);
  };

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | undefined>();

  const handleClose = () => {
    setAnchorEl(undefined);
  };

  const toggleDropdown = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);

    if (!isFetched) {
      refetch();
    }
  };

  const open = Boolean(anchorEl);

  return (
    <>
      <ListFilterSelectChipTrigger
        active={open}
        label={INTEGRATION_FILTER_LABEL}
        value={selectedOptions}
        onClear={handleOnClear}
        onClick={toggleDropdown}
        loading={isFetching}
      />
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <Box sx={{ width: 300 }}>
          <ListFilterMultiSelect
            options={options}
            value={selectedOptions}
            onSelect={handleOnSelect}
            enableSearch
            title="Select Integration Filter"
            loading={isFetching}
          />
        </Box>
      </Popover>
    </>
  );
}
